import React, { useMemo, useState } from 'react';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Space, Table, Tag, Tooltip } from 'antd';
import type { TableColumnsType, TableColumnType } from 'antd';
import { useSelector } from 'react-redux';

import {
  BUY,
  CANCELED,
  CREATED,
  EXECUTED,
  HISTORICAL_SCENARIO,
  ORDER_STATUSES,
  RESOURCE_TYPES,
  TRANSACTION_TYPES,
  selectors,
} from 'state';
import type { MarketRegimeSegmentType, Order, UnderlyingAsset } from 'state';
import { formatters } from 'services';
import {
  simplifiedOptionCurrencySymbol,
  simplifiedOptionSnapshotContracts,
} from '../../../state/app/simplified_options/presentation';
import { buildTradeAttributes } from '../../TrainerSession/AnalysisStudioLive/live/buildLive';
import type { TradeAttribute } from '../../TrainerSession/AnalysisStudioLive/live/types';
import {
  ATTRIBUTE_META,
  REGIME_META,
  makeRelativizer,
} from '../../TrainerSession/AnalysisStudioLive/helpers';
import {
  orderEventTimestamp,
  v5BlotterAssetLabel,
  v5TransactionLabel,
} from './model';
import { useTableScrollY } from './useTableScrollY';

const marketOrders = ['MarketOnOpenOrder', 'MarketOnCloseOrder'];
const limitOrders = ['LimitOrder', 'TakeProfitOrder', 'StopLossOrder'];
// Mirrors reports.constants.REGIME_COMPUTED. Pending/error reports must not
// expose an empty analytical column to the trainee.
const COMPUTED_MARKET_REGIME_STATUS = 1;

type DisplayRegime = {
  label: string;
  color: string;
  start_date: string;
  end_date: string;
};

type V5OrderRow = Order & {
  asset: UnderlyingAsset;
  asset_id: number;
  asset_label: string;
  currency: string;
  event_timestamp: string;
  value: number;
  is_simplified_option: boolean;
  lot_size: number;
  attribute: TradeAttribute | null;
  market_regime: DisplayRegime | null;
};

const assetSearchProps: Pick<
  TableColumnType<V5OrderRow>,
  'filterDropdown' | 'filterIcon' | 'onFilter'
> = {
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => (
    <div style={{ padding: 8 }} onKeyDown={event => event.stopPropagation()}>
      <Input
        autoFocus
        allowClear
        placeholder="Search asset"
        value={selectedKeys[0] as string}
        onChange={event =>
          setSelectedKeys(event.target.value ? [event.target.value] : [])
        }
        onPressEnter={() => confirm()}
        style={{ width: 180, marginBottom: 8 }}
      />
      <Space>
        <Button
          type="primary"
          size="small"
          icon={<SearchOutlined />}
          onClick={() => confirm()}
          style={{ width: 84 }}
        >
          Search
        </Button>
        <Button
          size="small"
          onClick={() => {
            clearFilters?.();
            confirm();
          }}
          style={{ width: 84 }}
        >
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: filtered => (
    <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
  ),
  onFilter: (value, record) =>
    record.asset_label.toLowerCase().includes(String(value).toLowerCase()),
};

const attributeTag = (attribute: TradeAttribute | null) => {
  if (!attribute) return <span className="blotter-v5__muted">—</span>;
  const meta = ATTRIBUTE_META[attribute];
  return (
    <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
      {meta.label}
    </Tag>
  );
};

const rationaleCell = (text?: string) =>
  text ? (
    <Tooltip title={text}>
      <span className="blotter-v5__ellipsis">{text}</span>
    </Tooltip>
  ) : (
    <span className="blotter-v5__muted">—</span>
  );

const transactionTag = (record: V5OrderRow) => {
  const label =
    v5TransactionLabel(record, record.is_simplified_option) ??
    TRANSACTION_TYPES[record.transaction];
  return (
    <Tag
      color={record.transaction === BUY ? 'green' : 'red'}
      style={{ marginInlineEnd: 0 }}
    >
      {label}
    </Tag>
  );
};

const marketRegimeFor = (
  order: Order,
  assetId: number,
  regimesByAsset: Map<number, MarketRegimeSegmentType[]>,
): DisplayRegime | null => {
  if (order.status !== EXECUTED) return null;
  const scenarioDate = (
    order.scenario_date || orderEventTimestamp(order)
  ).slice(0, 10);
  const segment = regimesByAsset
    .get(assetId)
    ?.find(
      candidate =>
        candidate.start_date <= scenarioDate &&
        scenarioDate <= candidate.end_date,
    );
  if (!segment) return null;

  const meta = REGIME_META[segment.regime] ?? {
    label: segment.regime,
    color: 'default',
  };
  return {
    ...meta,
    start_date: segment.start_date,
    end_date: segment.end_date,
  };
};

const Orders: React.FC = () => {
  const orders = useSelector(selectors.orders);
  const player = useSelector(selectors.player);
  const session = useSelector(selectors.activeSession);
  const financialInstruments = useSelector(selectors.financialInstruments);
  const currencies = useSelector(selectors.currencies);
  const underlyingAssets = useSelector(selectors.underlyingAssets);
  const persons = useSelector(selectors.persons);
  const { fetching_blotter: fetchingBlotter } = player;
  const isHistorical = session.scenario_type === HISTORICAL_SCENARIO;
  const hasComputedMarketRegimes =
    isHistorical &&
    session.market_regimes?.status === COMPUTED_MARKET_REGIME_STATUS;
  const relativizeDate = useMemo(
    () => makeRelativizer(session.start_date),
    [session.start_date],
  );
  const tableScroll = useTableScrollY();
  const [assetFilter, setAssetFilter] = useState<React.Key[]>([]);
  const [resourceFilter, setResourceFilter] = useState<React.Key[] | null>(
    null,
  );

  const attributes = useMemo(
    () => buildTradeAttributes(player, orders, isHistorical),
    [player, orders, isHistorical],
  );

  const regimesByAsset = useMemo(() => {
    const result = new Map<number, MarketRegimeSegmentType[]>();
    (session.market_regimes?.segments ?? []).forEach(segment => {
      const existing = result.get(segment.underlying_asset);
      if (existing) existing.push(segment);
      else result.set(segment.underlying_asset, [segment]);
    });
    return result;
  }, [session.market_regimes]);

  const data = useMemo<V5OrderRow[]>(
    () =>
      orders
        .filter(
          order =>
            String(order.resourcetype) !== 'SimplifiedOptionSettlementOrder',
        )
        .filter(order => order.status !== CREATED)
        .flatMap(order => {
          const instrument = financialInstruments[order.financial_instrument];
          const asset = instrument
            ? underlyingAssets[instrument.underlying_asset]
            : undefined;
          if (!instrument || !asset) return [];

          const isBond = asset.resourcetype.endsWith('Bond');
          const openPrice = Number(order.open_price ?? 0);
          const isSimplifiedOption = asset.resourcetype === 'SimplifiedOption';

          return [
            {
              ...order,
              asset,
              asset_id: asset.id,
              asset_label: v5BlotterAssetLabel(asset),
              currency: currencies[asset.variable_currency]?.symbol ?? '',
              event_timestamp: orderEventTimestamp(order),
              value: isBond
                ? (openPrice * order.quantity) / 100
                : openPrice * order.quantity,
              is_simplified_option: isSimplifiedOption,
              lot_size: asset.lot_size,
              attribute: attributes.get(order.id) ?? null,
              market_regime: hasComputedMarketRegimes
                ? marketRegimeFor(order, asset.id, regimesByAsset)
                : null,
            },
          ];
        }),
    [
      orders,
      financialInstruments,
      underlyingAssets,
      currencies,
      attributes,
      hasComputedMarketRegimes,
      regimesByAsset,
    ],
  );

  const showSimplifiedOptionColumns = data.some(
    order => order.is_simplified_option,
  );
  const executedCount = data.filter(order => order.status === EXECUTED).length;
  const canceledCount = data.filter(order => order.status === CANCELED).length;

  const cleanDirtyPrice = (record: V5OrderRow) => {
    const cleanPrice = Number(record.open_price ?? 0);
    const dirtyPrice = cleanPrice + Number(record.accrued_interest_orders ?? 0);
    return (
      <div>
        <div>Clean price: {formatters.commas2Digits(cleanPrice)}</div>
        <div>Dirty price: {formatters.commas2Digits(dirtyPrice)}</div>
        <div>*order execution done at dirty price,</div>
        <div> clean price given for trading reference</div>
      </div>
    );
  };

  const cleanDirtyValue = (record: V5OrderRow) => {
    const cleanPrice = Number(record.open_price ?? 0);
    const accrued = Number(record.accrued_interest_orders ?? 0);
    return (
      <div>
        <div>
          Clean value:{' '}
          {formatters.commasNoDigits((cleanPrice * record.quantity) / 100)}
        </div>
        <div>
          Dirty value:{' '}
          {formatters.commasNoDigits(
            ((cleanPrice + accrued) * record.quantity) / 100,
          )}
        </div>
      </div>
    );
  };

  const marketRegimeCell = (regime: DisplayRegime | null) => {
    if (!regime) return <span className="blotter-v5__muted">—</span>;
    const start = relativizeDate(regime.start_date) ?? 'Day —';
    const end = relativizeDate(regime.end_date) ?? 'Day —';
    return (
      <Tooltip title={`${start} → ${end}`}>
        <Tag color={regime.color} style={{ marginInlineEnd: 0 }}>
          {regime.label}
        </Tag>
      </Tooltip>
    );
  };

  const columns: TableColumnsType<V5OrderRow> = [
    {
      title: 'Asset',
      dataIndex: 'asset_label',
      key: 'asset_label',
      fixed: 'left',
      width: 106,
      sorter: (a, b) => a.asset_label.localeCompare(b.asset_label),
      ...assetSearchProps,
      filteredValue: assetFilter.length ? assetFilter : null,
      onCell: record => ({
        onClick: () =>
          setAssetFilter(current =>
            current.length === 1 && current[0] === record.asset_label
              ? []
              : [record.asset_label],
          ),
        style: { cursor: 'pointer' },
      }),
      render: (label: string) => {
        const active = assetFilter.length === 1 && assetFilter[0] === label;
        return (
          <Tooltip
            placement="left"
            title={
              active
                ? 'Filtering by this asset — click to clear'
                : 'Click to filter the blotter by this asset'
            }
          >
            <span className="blotter-v5__asset-link">{label}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Txn',
      dataIndex: 'transaction',
      key: 'transaction',
      width: 84,
      sorter: (a, b) => a.transaction - b.transaction,
      render: (_transaction, record) => transactionTag(record),
    },
    {
      title: 'Trade Attribute',
      dataIndex: 'attribute',
      key: 'attribute',
      width: 120,
      render: attributeTag,
    },
    {
      title: 'Ccy',
      dataIndex: 'currency',
      key: 'currency',
      width: 62,
      sorter: (a, b) => a.currency.localeCompare(b.currency),
    },
    {
      title: 'Price',
      dataIndex: 'open_price',
      key: 'open_price',
      width: 92,
      align: 'right',
      render: (value, record) => {
        if (record.is_simplified_option) return '—';
        let displayValue = value;
        if (record.status === CANCELED) {
          if (marketOrders.includes(record.resourcetype)) return '—';
          if (limitOrders.includes(record.resourcetype)) {
            displayValue = record.limit_price;
          }
        }
        return (
          <span>
            {formatters.priceFormat(
              displayValue,
              record.asset.ticker,
              record.currency,
              record.asset.resourcetype,
            )}
            {record.status !== CANCELED &&
              record.asset.resourcetype.endsWith('Bond') && (
                <Popover placement="bottom" content={cleanDirtyPrice(record)}>
                  <InfoCircleOutlined className="blotter-v5__info" />
                </Popover>
              )}
          </span>
        );
      },
    },
    ...(showSimplifiedOptionColumns
      ? [
          {
            title: 'Strike',
            key: 'simplified_option_strike',
            width: 88,
            align: 'right' as const,
            render: (_value: unknown, record: V5OrderRow) => {
              const strike = record.simplified_option_snapshot?.raw_strike;
              return strike == null ? '—' : formatters.commas2Digits(strike);
            },
          },
          {
            title: 'Annualized IV',
            key: 'simplified_option_volatility',
            width: 105,
            align: 'right' as const,
            render: (_value: unknown, record: V5OrderRow) => {
              const volatility =
                record.simplified_option_snapshot
                  ?.annualized_implied_volatility;
              return volatility == null
                ? '—'
                : formatters.percent2Digit(volatility);
            },
          },
        ]
      : []),
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 98,
      align: 'right',
      render: (value, record) => {
        const displayQuantity = record.is_simplified_option
          ? simplifiedOptionSnapshotContracts(record.simplified_option_snapshot)
          : record.asset.resourcetype === 'Futures'
            ? formatters.unitsToContracts(value, record.lot_size)
            : value;
        return `${record.transaction === BUY ? '+' : '-'}${formatters.commasNoDigits(
          displayQuantity,
        )}`;
      },
    },
    ...(showSimplifiedOptionColumns
      ? [
          {
            title: 'Premium',
            key: 'simplified_option_premium',
            width: 100,
            align: 'right' as const,
            render: (_value: unknown, record: V5OrderRow) => {
              const premium =
                record.simplified_option_snapshot?.signed_total_premium;
              return premium == null ? '—' : formatters.commas2Digits(premium);
            },
          },
          {
            title: 'Expiry days',
            key: 'simplified_option_expiry_days',
            width: 92,
            align: 'right' as const,
            render: (_value: unknown, record: V5OrderRow) =>
              record.simplified_option_snapshot?.days_to_expiry ?? '—',
          },
          {
            title: 'Payoff',
            key: 'simplified_option_payoff',
            width: 110,
            align: 'right' as const,
            render: (_value: unknown, record: V5OrderRow) => {
              const snapshot = record.simplified_option_snapshot;
              if (!snapshot || snapshot.display_payoff == null) return '—';
              const symbol = simplifiedOptionCurrencySymbol(
                snapshot.native_currency,
                currencies,
              );
              return `${formatters.commas2Digits(
                snapshot.display_payoff,
              )} ${symbol}`;
            },
          },
        ]
      : []),
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 110,
      align: 'right',
      render: (value, record) => {
        if (record.is_simplified_option) return '—';
        return (
          <span>
            {formatters.priceFormat(
              value,
              record.asset.ticker,
              '',
              record.asset.resourcetype,
            )}
            {record.status !== CANCELED &&
              record.asset.resourcetype.endsWith('Bond') && (
                <Popover placement="bottom" content={cleanDirtyValue(record)}>
                  <InfoCircleOutlined className="blotter-v5__info" />
                </Popover>
              )}
          </span>
        );
      },
    },
    ...(hasComputedMarketRegimes
      ? [
          {
            title: 'Market regime',
            dataIndex: 'market_regime',
            key: 'market_regime',
            width: 132,
            render: (regime: DisplayRegime | null) => marketRegimeCell(regime),
          },
        ]
      : []),
    {
      title: isHistorical ? 'Day' : 'Timestamp',
      key: 'event_timestamp',
      width: isHistorical ? 176 : 154,
      defaultSortOrder: 'descend',
      sorter: (a, b) => {
        if (a.event_timestamp !== b.event_timestamp) {
          return a.event_timestamp.localeCompare(b.event_timestamp);
        }
        return a.id - b.id;
      },
      render: (_value, record) => {
        const wallClock = record.event_timestamp
          ? formatters.dateWithTimeSeconds(record.event_timestamp)
          : '—';
        if (!isHistorical) return wallClock;
        return (
          <div className="blotter-v5__time">
            <span>
              {record.status === CANCELED
                ? '—'
                : (relativizeDate(record.scenario_date) ?? 'Day —')}
            </span>
            <span className="blotter-v5__time-secondary">{wallClock}</span>
          </div>
        );
      },
    },
    {
      title: 'Rationale',
      dataIndex: 'investment_rationale',
      key: 'investment_rationale',
      width: 190,
      render: rationaleCell,
    },
    {
      title: 'Order type',
      dataIndex: 'resourcetype',
      key: 'resourcetype',
      width: 130,
      sorter: (a, b) => a.resourcetype.localeCompare(b.resourcetype),
      filters: RESOURCE_TYPES.map(resource => ({
        text: resource,
        value: resource,
      })),
      filteredValue: resourceFilter,
      onFilter: (value, record) => value === record.resourcetype,
    },
    {
      title: 'User',
      dataIndex: 'person',
      key: 'person',
      width: 110,
      render: person => persons?.[person]?.name || person,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      width: 92,
      sorter: (a, b) => a.status - b.status,
      render: status => (
        <Tag
          color={status === CANCELED ? 'default' : 'blue'}
          style={{ marginInlineEnd: 0 }}
        >
          {ORDER_STATUSES[status as keyof typeof ORDER_STATUSES]}
        </Tag>
      ),
    },
  ];

  return (
    <section className="blotter-v5__card" aria-label="Orders blotter">
      <header className="blotter-v5__card-head">
        <span className="blotter-v5__card-title">
          Orders
          {data.length > 0 && (
            <span className="blotter-v5__card-hint">
              {' — '}click an asset to filter
            </span>
          )}
        </span>
        <span className="blotter-v5__count">
          {executedCount} executed · {canceledCount} canceled
        </span>
      </header>
      <div ref={tableScroll.ref} className="blotter-v5__table-wrap">
        <Table<V5OrderRow>
          size="small"
          rowKey="id"
          loading={fetchingBlotter}
          columns={columns}
          dataSource={data}
          pagination={false}
          scroll={{ x: 'max-content', y: tableScroll.y }}
          rowClassName={record =>
            record.status === CANCELED ? 'blotter-v5__row--canceled' : ''
          }
          onChange={(_pagination, filters) => {
            setAssetFilter((filters.asset_label as React.Key[]) ?? []);
            setResourceFilter(
              (filters.resourcetype as React.Key[] | null) ?? null,
            );
          }}
        />
      </div>
    </section>
  );
};

export default Orders;
